import { gql } from "@apollo/client";
import { ActorRefFrom } from "xstate";
import { createModel } from "xstate/lib/model";

import { graphQLClient } from "../../api/graphql";
import { openReportURL, SelectedSupplier, SupplierFilter } from "../generateReportConfig.machine";

export interface DeliveryNotesReportActor extends ActorRefFrom<typeof deliveryNotesReportMachine> {}

export const deliveryNotesReportModel = createModel(
  {
    dateRange: {
      startDate: new Date(),
      endDate: new Date(),
    },
    supplierFilter: undefined as SupplierFilter | undefined,
    reportURL: undefined as string | undefined,
    error: undefined as string | undefined,
  },
  {
    events: {
      provideReportDates: (value: { startDate: Date; endDate: Date }) => ({ data: value }),
      provideSupplierFilter: (value: { from: SelectedSupplier; to: SelectedSupplier }) => ({
        data: value,
      }),
      skipStep: () => ({}),
      goBack: () => ({}),
      generateReport: () => ({}),
      reportGenerationSuccess: (value: string) => ({ data: { reportURL: value } }),
      reportGenerationFailure: (value: string) => ({ data: { error: value } }),
      restart: () => ({}),
    },
  }
);

export const deliveryNotesReportMachine = deliveryNotesReportModel.createMachine(
  {
    context: deliveryNotesReportModel.initialContext,
    initial: "configuringDateRange",
    states: {
      configuringDateRange: {
        on: {
          provideReportDates: {
            actions: deliveryNotesReportModel.assign({
              dateRange: (_, ev) => {
                return ev.data;
              },
            }),
            target: "configuringSupplierFilter",
          },
        },
      },
      configuringSupplierFilter: {
        id: "configuringSupplierFilter",
        on: {
          provideSupplierFilter: {
            actions: deliveryNotesReportModel.assign({
              supplierFilter: (_, ev) => {
                return ev.data;
              },
            }),
            target: "showingReportConfigSummary",
          },
          skipStep: {
            actions: deliveryNotesReportModel.assign({
              supplierFilter: () => undefined,
            }),
            target: "showingReportConfigSummary",
          },
          goBack: {
            target: "configuringDateRange",
          },
        },
      },
      showingReportConfigSummary: {
        initial: "idle",
        states: {
          idle: {
            on: {
              generateReport: {
                target: "generatingReport",
              },
              goBack: {
                target: "#configuringSupplierFilter",
              },
            },
          },
          generatingReport: {
            invoke: {
              id: "generatingReport",
              src: "generateReport",
              onError: {
                target: "#failure",
              },
            },
            on: {
              reportGenerationSuccess: {
                target: "#reportGenerated",
                actions: deliveryNotesReportModel.assign({
                  reportURL: (_, ev) => ev.data.reportURL,
                }),
              },
              reportGenerationFailure: {
                target: "#failure",
                actions: deliveryNotesReportModel.assign({
                  error: (_, ev) => ev.data.error,
                }),
              },
            },
          },
        },
      },
      reportGenerated: {
        id: "reportGenerated",
        entry: "openReportURL",
      },
      failure: {
        id: "failure",
        on: {
          restart: {
            target: "configuringDateRange",
            actions: deliveryNotesReportModel.assign(() => {
              return deliveryNotesReportModel.initialContext;
            }),
          },
        },
      },
    },
  },
  {
    actions: {
      openReportURL: async (ctx) => {
        if (!ctx.reportURL) {
          throw new Error("Response does not contain a url");
        }

        openReportURL(ctx.reportURL);
      },
    },
    services: {
      generateReport: (ctx, ev) => (cb) => {
        graphQLClient
          .query({
            query: GENERATE_DELIVERY_NOTES_REPORT,
            variables: {
              input: {
                startDate: ctx.dateRange.startDate,
                endDate: ctx.dateRange.endDate,
                fromSupplierID: ctx.supplierFilter?.from.internalId,
                toSupplierID: ctx.supplierFilter?.to.internalId,
              },
            },
          })
          .then((res) => {
            const reportUrl = res.data.deliveryCollectionNotesReport.url;

            cb(deliveryNotesReportModel.events.reportGenerationSuccess(reportUrl));
          })
          .catch((e) => {
            cb(deliveryNotesReportModel.events.reportGenerationFailure(e.message));
          });
      },
    },
  }
);

const GENERATE_DELIVERY_NOTES_REPORT = gql`
  query GetDeliveryCollectionNotesReportInfo($input: DeliveryCollectionNotesReportInput!) {
    deliveryCollectionNotesReport(input: $input) {
      url
    }
  }
`;
